'use client';

import { Container } from '@coolblue-development/freddy';
import { mapFragmentToProps } from '@coolblue-development/next-insurance/insurance-selector';
import { AddToCartPageSectionTypes } from '@features/add-to-cart-page/typings/AddToCartPageSection';
import { ATCPageSections } from '@features/add-to-cart-page/typings/AddToCartPageStep';
import { ProductSummary } from '@features/add-to-cart-page/typings/Product';

import { AutomatedSection } from './AutomatedSection';
import { ClassificationOptionsRowSection } from './ClassificationOptionsRowSection';
import { CustomerContextProvider } from './CustomerContext/CustomerContext';
import { InsuranceSection } from './InsuranceSection/InsuranceSection';
import { ManualOptionsRowSection } from './ManualOptionsRowSection';
import { ProductTypeSection } from './ProductTypeSection';

type AddToCartSectionsProps = {
  product: ProductSummary;
  sections: ATCPageSections;
  isAddedAsSubscription: boolean;
  isBusinessCustomer: boolean;
  hideHighlightedSegments?: boolean;
  shouldUsePromotionalContent: boolean;
};

function AddToCartSections({
  product,
  sections,
  isAddedAsSubscription,
  isBusinessCustomer,
  hideHighlightedSegments = false,
  shouldUsePromotionalContent
}: AddToCartSectionsProps) {
  let order = 1;

  function shouldRenderInsuranceSection() {
    if (isAddedAsSubscription) return false;
    if (isBusinessCustomer) return false;

    if (product.insurance) return true;
    if (product.partnerInsurance) return true;

    return false;
  }

  const content = sections.map((section, index) => {
    let sectionComponent = null;
    switch (section.__typename) {
      case AddToCartPageSectionTypes.AUTOMATED_SECTION:
        sectionComponent = (
          <AutomatedSection
            key={index}
            order={order}
            product={product}
            section={section}
            isBusiness={isBusinessCustomer}
            shouldUsePromotionalContent={shouldUsePromotionalContent}
          />
        );
        break;

      case AddToCartPageSectionTypes.CLASSIFICATION_OPTIONS_ROW_SECTION:
        sectionComponent = (
          <ClassificationOptionsRowSection
            key={index}
            order={order}
            product={product}
            section={section}
            isBusiness={isBusinessCustomer}
            shouldUsePromotionalContent={shouldUsePromotionalContent}
          />
        );
        break;

      case AddToCartPageSectionTypes.MANUAL_OPTIONS_ROW_SECTION:
        sectionComponent = (
          <ManualOptionsRowSection
            key={index}
            order={order}
            product={product}
            section={section}
            isBusiness={isBusinessCustomer}
            shouldUsePromotionalContent={shouldUsePromotionalContent}
          />
        );
        break;

      case AddToCartPageSectionTypes.PRODUCT_TYPE_SECTION:
        sectionComponent = (
          <ProductTypeSection
            key={index}
            order={order}
            product={product}
            section={section}
            isBusiness={isBusinessCustomer}
            hideHighlightedSegments={hideHighlightedSegments}
            shouldUsePromotionalContent={shouldUsePromotionalContent}
          />
        );
        break;

      case AddToCartPageSectionTypes.INSURANCE_SECTION: {
        const props = mapFragmentToProps({
          insurance: product?.insurance ?? null,
          partnerInsurance: product?.partnerInsurance ?? null
        });

        sectionComponent =
          shouldRenderInsuranceSection() && props ? (
            <InsuranceSection
              key={index}
              order={order}
              productId={product.id}
              productType={product.productType?.singularName}
              {...props}
            />
          ) : null;

        break;
      }

      default:
        break;
    }

    if (sectionComponent) {
      order++;
      return sectionComponent;
    }

    return undefined;
  });

  return (
    <Container>
      <CustomerContextProvider isBusinessCustomer={isBusinessCustomer}>
        {content}
      </CustomerContextProvider>
    </Container>
  );
}

export default AddToCartSections;
